import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Loader2,
  Mic2,
  Moon,
  Radio,
  Square,
  Volume2,
} from "lucide-react";
import { formatRelativeTime } from "@/utils";
import type {
  CaptureActivity,
  CaptureActivityDiagnostic,
  CaptureActivitySecondaryStatus,
  CaptureActivityState,
} from "@/data/spoken-memory";

interface CaptureActivityPanelProps {
  activities: CaptureActivity[];
  showDiagnostics?: boolean;
}

type StateTone =
  | "active"
  | "waiting"
  | "processing"
  | "ready"
  | "paused"
  | "attention"
  | "idle";

const stateCopy: Record<
  CaptureActivityState,
  {
    label: string;
    tone: StateTone;
    Icon: typeof Radio;
  }
> = {
  ambient_armed: { label: "Ambient armed", tone: "waiting", Icon: Radio },
  ambient_capturing: { label: "Ambient capturing", tone: "active", Icon: Mic2 },
  explicit_recording: {
    label: "Manual recording",
    tone: "active",
    Icon: Square,
  },
  hearing_speech: { label: "Hearing speech", tone: "active", Icon: Mic2 },
  settling: { label: "Waiting for quiet", tone: "waiting", Icon: Clock3 },
  processing: { label: "Processing memory", tone: "processing", Icon: Loader2 },
  ready_recently: { label: "Memory ready", tone: "ready", Icon: CheckCircle2 },
  continuous_audio: {
    label: "Continuous audio",
    tone: "paused",
    Icon: Volume2,
  },
  needs_attention: {
    label: "Needs attention",
    tone: "attention",
    Icon: AlertTriangle,
  },
  idle: { label: "Idle", tone: "idle", Icon: Moon },
  offline: { label: "Offline", tone: "idle", Icon: Radio },
};

const toneClasses: Record<StateTone, string> = {
  active: "border-emerald-400/40 bg-emerald-400/10 text-emerald-100",
  waiting: "border-amber-300/40 bg-amber-300/10 text-amber-100",
  processing: "border-sky-300/40 bg-sky-300/10 text-sky-100",
  ready: "border-lime-300/40 bg-lime-300/10 text-lime-100",
  paused: "border-orange-300/40 bg-orange-300/10 text-orange-100",
  attention: "border-red-300/45 bg-red-300/10 text-red-100",
  idle: "border-wiki-border bg-wiki-bg-hover text-wiki-fg-2",
};

export function CaptureActivityPanel({
  activities,
  showDiagnostics = true,
}: CaptureActivityPanelProps) {
  if (activities.length === 0) {
    return (
      <section className="rounded-xl border border-wiki-border bg-wiki-bg-2/30 p-4">
        <div className="label-caps">Capture activity</div>
        <div className="mt-2 text-sm text-wiki-fg-3">
          Linked devices will show live capture activity here.
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-wiki-border bg-wiki-bg-2/30 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="label-caps">Capture activity</div>
          <h2 className="text-lg font-semibold text-wiki-fg-1">
            Live device state
          </h2>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {activities.map((activity) => (
          <ActivityCard
            key={activity.device_id}
            activity={activity}
            showDiagnostics={showDiagnostics}
          />
        ))}
      </div>
    </section>
  );
}

function ActivityCard({
  activity,
  showDiagnostics,
}: {
  activity: CaptureActivity;
  showDiagnostics: boolean;
}) {
  const state = stateCopy[activity.state];
  const Icon = state.Icon;
  const lastActivity =
    activity.last_activity_at == null
      ? null
      : formatRelativeTime(activity.last_activity_at);
  const secondaryStatuses = activity.secondary_statuses ?? [];

  return (
    <article className="rounded-lg border border-wiki-border bg-wiki-bg-3/45 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-wiki-fg-1">
            {activity.device_name ?? activity.device_id}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${toneClasses[state.tone]}`}
            >
              <Icon
                aria-hidden="true"
                className={`h-3.5 w-3.5 ${activity.state === "processing" ? "animate-spin" : ""}`}
              />
              {state.label}
            </span>
            {lastActivity ? (
              <span className="text-xs text-wiki-fg-3">{lastActivity}</span>
            ) : null}
          </div>
          <div className="mt-2 text-sm text-wiki-fg-2">{activity.headline}</div>
          <div className="mt-1 text-xs leading-relaxed text-wiki-fg-3">
            {activity.detail}
          </div>
          {secondaryStatuses.length > 0 ? (
            <div className="mt-3 space-y-1.5">
              {secondaryStatuses.map((status) => (
                <SecondaryStatusRow
                  key={secondaryStatusKey(status)}
                  status={status}
                />
              ))}
            </div>
          ) : null}
          {showDiagnostics && activity.diagnostics.length > 0 ? (
            <div className="mt-3 space-y-2">
              {activity.diagnostics.map((diagnostic) => (
                <DiagnosticRow
                  key={`${diagnostic.kind}:${diagnostic.window_id}`}
                  diagnostic={diagnostic}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function SecondaryStatusRow({
  status,
}: {
  status: CaptureActivitySecondaryStatus;
}) {
  const state = stateCopy[status.state];
  const Icon = state.Icon;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-wiki-fg-3">
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-medium ${toneClasses[state.tone]}`}
      >
        <Icon
          aria-hidden="true"
          className={`h-3.5 w-3.5 ${status.state === "processing" ? "animate-spin" : ""}`}
        />
        {status.label}
      </span>
      <span>{status.detail}</span>
    </div>
  );
}

function secondaryStatusKey(status: CaptureActivitySecondaryStatus): string {
  return [
    status.state,
    status.active_window_id,
    status.active_session_id,
    status.occurred_at,
  ].join(":");
}

function DiagnosticRow({
  diagnostic,
}: {
  diagnostic: CaptureActivityDiagnostic;
}) {
  const occurredAt =
    diagnostic.occurred_at == null
      ? null
      : formatRelativeTime(diagnostic.occurred_at);
  return (
    <div className="rounded-md border border-wiki-border bg-wiki-bg-2/60 px-2 py-1.5 text-xs leading-relaxed text-wiki-fg-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-wiki-fg-2">Older capture issue</span>
        {occurredAt ? <span>{occurredAt}</span> : null}
      </div>
      <div className="mt-1">
        One older capture did not finish processing. New recordings are
        unaffected.
      </div>
    </div>
  );
}
